/**
 * Logged time on a project, and turning the billable part of it into an invoice.
 *
 * The invoice button is the reason this screen exists rather than a column
 * somewhere: it is the only place in the CRM where hours become money, and it
 * needs the hours in front of it while it does. Which is also why the button
 * answers to a different right than the tab around it - creating an invoice is
 * `manage:crm.sales`, and a project manager can perfectly well be without it.
 * The operator comes from `useCrmUser` rather than through three components
 * that have no other use for it.
 *
 * The button no longer bills anything itself: it opens `InvoiceTimeModal`, and
 * the POST, its options and every refusal it can answer with live there. What
 * stays here is what the screen owns - reloading the list and the figures once
 * a document exists, and telling the page around it that the project's money
 * moved. See that file for why a refusal keeps the dialog open.
 *
 * THE FIGURE STRIP IS A STATEMENT ABOUT THE PROJECT, and the filter below it is
 * a question about the list. Keeping those two apart is now the arrangement of
 * the whole screen. All three figures come from the project summary: not one
 * running total but `logged`, `billed` and `unbilled`, because unbilled is the
 * number the button spends and so has to be next to it - "invoice time" with
 * only a grand total beside it is a button you press and then go looking for
 * what it did.
 *
 * The list's own `totalMinutes` used to supply the first of them, and no longer
 * does. That number narrows with the person filter while the other two cannot
 * (the list endpoint has no billable/invoiced filter to count with), so leaving
 * it there would have put one person's hours beside two project figures under
 * three labels that all read the same way. Three figures from one source say one
 * thing; when the summary fails they are absent together, which is a state a
 * reader can name. The filtered set's own total is deliberately NOT printed
 * anywhere: there is no label in the catalogue that would tell it apart from the
 * project figure above it, and a second unlabelled total is the exact blur this
 * strip was built to avoid.
 *
 * The person filter itself is the first caller the endpoint's `userId` parameter
 * has ever had. It cannot ask for the unowned rows, and that is a limit of the
 * route rather than a choice here: every filter there sits behind a truthiness
 * check, so an empty string reads as "no filter" and would answer with the whole
 * list. It would also be a filter that changes nothing today - all 185 imported
 * entries are unowned, so "nobody" and "everyone" are the same set until
 * somebody logs an hour from this screen.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Clock, Loader2, Receipt } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform } from "../../../lib/auth";
import {
  deleteTimeEntry, getProjectSummary, getTasks, getTimeEntries,
  type ProjectSummary, type TimeEntry,
} from "../../../lib/projects";
import { staffOptionLabel } from "../../../lib/staff";
import { useCrmUser } from "../../shell/CrmUser";
import { Btn, Card, EmptyState, MenuField, Toolbar, useToast, listCard } from "../../ui";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { useStaff } from "../_shared/staff";
import { InvoiceTimeModal } from "./InvoiceTimeModal";
import { fmtHours } from "./lib";
import { TimeEntryForm, type TaskOption } from "./TimeEntryForm";
import { TimeEntryEditModal } from "./TimeEntryEditModal";
import { TIME_COLS, TimeEntryHead, TimeEntryRow } from "./TimeEntryRow";

/** How many rows one read brings back, and how much each "more" adds. */
const PAGE = 100;

/** The ceiling both list routes put on `pageSize` (`time.routes.ts` and
 *  `tasks.routes.ts` agree on 500), so the window stops growing there and the
 *  footer keeps telling the truth about what is left. 185 time entries is the
 *  whole Perfex history across every project, so this is a guardrail rather
 *  than a limit anybody is expected to meet. */
const MAX_ROWS = 500;

/** The filter's own value for "not filtering", which never reaches the API.
 *  Spelled out locally rather than shared with the route: the client and the API
 *  are two deployables, and a constant carried across that line is a constant
 *  that looks synchronised without being it. */
const ALL = "all";

/** One figure from the strip over the list, in the same dress as the KPI row on
 *  the project head, so a number means the same thing wherever it is met. */
function Figure({ label, value }: { label: string; value: string }) {
  return (
    <span>
      <span className="crm-q-lab block">{label}</span>
      <span className="crm-q-val crm-tnum block">{value}</span>
    </span>
  );
}

export function TimeEntries({ projectId, onChange }: { projectId: string; onChange: () => void }) {
  const t = useT();
  const toast = useToast();
  const canSell = userCanPlatform(useCrmUser(), "manage:crm.sales");
  const { staff } = useStaff();
  const [rows, setRows] = useState<TimeEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(PAGE);
  const [who, setWho] = useState(ALL);
  const [summary, setSummary] = useState<ProjectSummary | null>(null);
  const [tasks, setTasks] = useState<TaskOption[]>([]);
  const [editing, setEditing] = useState<TimeEntry | null>(null);
  const [deleting, setDeleting] = useState<TimeEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  // The pickers' options, read once per project: tasks do not change because
  // somebody logged an hour against one, so this deliberately sits outside
  // `load`. `pageSize` is the route's own ceiling - a project with more open
  // tasks than that has a bigger problem than this select.
  useEffect(() => {
    let alive = true;
    getTasks({ projectId, pageSize: MAX_ROWS })
      .then((r) => {
        if (alive) setTasks(r.rows.map((tk) => ({ id: tk.id, title: tk.title })));
      })
      .catch(() => {
        if (alive) setTasks([]);
      });
    return () => {
      alive = false;
    };
  }, [projectId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        getTimeEntries({ projectId, pageSize: limit, userId: who === ALL ? undefined : who }),
        // Allowed to fail on its own: three figures missing is better than three
        // figures invented, and the list is still readable without them. It is
        // also NOT narrowed by the filter - see the file comment.
        getProjectSummary(projectId).catch(() => null),
      ]);
      setRows(r.rows);
      setTotal(r.total);
      setSummary(s);
    } finally {
      setLoading(false);
    }
  }, [projectId, limit, who]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Everyone, then every person the directory holds.
   *
   * A person who has left keeps their name and gains the word: their hours are
   * still on last year's projects, so filtering by them has to stay possible
   * even though giving them new work does not. `staffOptionLabel` is how the
   * pickers that cannot draw their own rows say that (`MenuField` takes a string
   * and nothing else), and `useStaff` has already sorted the leavers last.
   */
  const people = useMemo(
    () => [
      { value: ALL, label: t("projects.filterUser") },
      ...staff.map((s) => ({ value: s.id, label: staffOptionLabel(s, t("common.inactive")) })),
    ],
    [staff, t],
  );

  async function remove(id: string) {
    try {
      await deleteTimeEntry(id);
      await load();
      onChange();
    } catch (err) {
      // The menu does not offer delete on an invoiced row, but the API is the
      // guard and it can refuse a row that was invoiced since this list loaded.
      const e = err as { body?: { error?: string } };
      toast(e.body?.error === "entry_invoiced" ? t("projects.time.invoicedLocked") : t("common.error"));
    }
  }

  return (
    <>
      <TimeEntryForm
        projectId={projectId}
        tasks={tasks}
        onAdded={async () => {
          await load();
          onChange();
        }}
      />

      {/* Drawn only when it has something in it. Without the summary and without
          the sales right there is nothing left but an empty white box, which
          reads as a card that failed to load. */}
      {summary || canSell ? (
        <Card className="mb-4 flex flex-wrap items-center gap-4 px-6 py-5">
          {summary ? (
            <>
              <Figure label={t("projects.totalLogged")} value={`${fmtHours(summary.totalMinutes)} h`} />
              <Figure label={t("projects.billedHours")} value={`${fmtHours(summary.billedMinutes)} h`} />
              <Figure label={t("projects.unbilledHours")} value={`${fmtHours(summary.unbilledMinutes)} h`} />
            </>
          ) : null}
          {canSell ? (
            <Btn className="crm-push" onClick={() => setInvoiceOpen(true)}>
              <Receipt className="size-4" />
              {t("projects.invoiceTime")}
            </Btn>
          ) : null}
        </Card>
      ) : null}

      {/* Outside the card, and outside the loading and empty branches inside it:
          a filter that disappears the moment it returns nothing is a filter the
          operator cannot undo. It is hidden only when the directory itself came
          back empty - `useStaff` answers a failed fetch with no rows, and a lone
          "Everyone" pill would then be a control with nothing to choose. The
          project head above has already warmed that cache with the team strip,
          so in practice this is drawn on the first frame. */}
      {staff.length > 0 ? (
        <Toolbar>
          <MenuField
            value={who}
            onChange={(v) => {
              setWho(v);
              // Back to one page: the window the operator grew belongs to the
              // question they were asking, not to the next one.
              setLimit(PAGE);
            }}
            label={t("projects.loggedBy")}
            options={people}
          />
        </Toolbar>
      ) : null}

      <div className="crm-card" style={listCard(TIME_COLS)}>
        {loading && rows.length === 0 ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Clock} title={t("projects.noTime")} />
        ) : (
          <>
            <TimeEntryHead />

            <div className="crm-rows pt-0">
              {rows.map((e) => (
                <TimeEntryRow
                  key={e.id}
                  entry={e}
                  onEdit={() => setEditing(e)}
                  onDelete={() => setDeleting(e)}
                />
              ))}
            </div>

            {/* The list is a window, and it used to close over the rest without
                a word: a project with 120 entries showed 100 while the total
                above it counted all 120, so the two disagreed and only one of
                them said so. Both halves narrow with the person filter, so the
                sentence stays true of whatever is being asked for. */}
            <div className="crm-tfoot">
              {t("projects.showingTime", { shown: rows.length, n: total })}
              {rows.length < total && limit < MAX_ROWS ? (
                <Btn
                  small
                  className="crm-push"
                  disabled={loading}
                  onClick={() => setLimit((l) => Math.min(MAX_ROWS, l + PAGE))}
                >
                  {loading ? <Loader2 className="size-4 animate-spin" /> : null}
                  {t("common.more")}
                </Btn>
              ) : null}
            </div>
          </>
        )}
      </div>

      {/* The dialog owns the call and its own success toast, because it is the
          only side that knows the invoice number. What comes back here is the
          fact that a document now exists: reload the list and the three figures
          (unbilled hours have just moved to billed) and tell the page around
          this tab, whose head carries the same money. */}
      {invoiceOpen ? (
        <InvoiceTimeModal
          projectId={projectId}
          onClose={() => setInvoiceOpen(false)}
          onDone={() => {
            void load();
            onChange();
          }}
        />
      ) : null}

      {/* Asked rather than done. This menu item used to delete on the click, and
          the hours it removes are not recoverable from any screen: they leave
          the project total, leave whatever would have been invoiced, and the
          only surviving record is an audit row nobody reads by accident. The
          task page asks the same question with the same words, because one
          action with two behaviours is worse than either of them. */}
      {deleting ? (
        <ConfirmModal
          title={t("projects.time.deleteTitle")}
          body={t("projects.time.deleteConfirm", { hours: fmtHours(deleting.minutes) })}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            await remove(deleting.id);
            setDeleting(null);
          }}
          onClose={() => setDeleting(null)}
        />
      ) : null}

      {editing ? (
        <TimeEntryEditModal
          entry={editing}
          tasks={tasks}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void load();
            onChange();
          }}
        />
      ) : null}
    </>
  );
}
